/**
 * The gallery's agents in both directions: buyer agents the firm has sent
 * work out to, and the sellers who are bringing work in.
 *
 * Pure. The roster and the unanswered offers go in; two grouped lists, three
 * counts and the chosen side's empty sentence come out. The copy is
 * `galleryAgents.constants.ts`; the two reads, the recall write and the
 * side and fold state are `useGalleryAgentsController.ts`. Appointing an
 * agent and answering an offer are deliberately not here — the first lives
 * on the work's record and the second on Inventory, beside the terms.
 *
 * The side is a filter over loaded rows, not a route. A placement row
 * carries the date and the gallery's note and no status pill, because what
 * an agent is doing with a work is their report and nothing in the schema
 * observes it. Folded groups are keyed on "closed" so a new arrival opens.
 */

import type { ConsignedWork, GalleryAgentPlacement } from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { formatLongDay } from "../lib/dates.ts";
import { initialsOf } from "../lib/names.ts";
import { plural } from "../lib/text.ts";
import type { Tile } from "../lib/Tiles.tsx";
import { GALLERY_AGENTS_COPY } from "./galleryAgents.constants.ts";

/**
 * Which half of the relation the reader is looking at.
 *
 * NOT two screens and not two routes. A buyer agent is a mandate this gallery
 * GRANTED and a seller agent is a counterparty who brought it a work, and both
 * are readings of rows the registry already holds — so the side is a filter over
 * loaded data, exactly as the artist chips on Inventory are. `records.agents` is
 * `"none"` in the portal spec, so there is no sub-route to land on either.
 */
export type AgentSide = "buyer" | "seller";

/** One work this gallery has sent out, as the row prints it. */
export interface PlacementRow {
  mandateId: string;
  workId: string;
  href: string;
  title: string;
  /** 'Peter Doig · 2003' — the line under the title. */
  attribution: string;
  /**
   * 'Sent 22 July 2026 · Client viewing in New York, wk of 28 Jul.'
   *
   * The date and the gallery's own note, and DELIBERATELY NO STATUS. What an
   * agent is doing with a work is the agent's report; a pill would make it read
   * as something the registry observed, and nothing in this schema observes it.
   */
  sent: string;
  imageUrl: string | null;
  canEnd: boolean;
}

/** One agent, and everything this gallery has put in their hands. */
export interface AgentGroup {
  key: string;
  ondoId: string;
  name: string;
  initials: string;
  /** 'Advisory · ONDO-8473' or 'Agent · ONDO-2241'. */
  meta: string;
  verified: boolean;
  rows: PlacementRow[];
  open: boolean;
}

/** One work a vendor has offered, grouped under whoever offered it. */
export interface OfferRow {
  consignmentId: string;
  workId: string;
  href: string;
  title: string;
  attribution: string;
  imageUrl: string | null;
}

/** One counterparty bringing works to this gallery. */
export interface SellerGroup {
  key: string;
  ondoId: string;
  name: string;
  initials: string;
  meta: string;
  rows: OfferRow[];
  open: boolean;
}

export type GalleryAgentsView =
  | { kind: "loading"; message: string }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      tiles: Tile[];
      side: AgentSide;
      /** The chip labels, with the seller count on it as the design has. */
      buyerLabel: string;
      sellerLabel: string;
      buyers: AgentGroup[];
      sellers: SellerGroup[];
      /** Set when the chosen side has nothing in it. */
      empty: string | null;
    };

/**
 * A gallery's agents, both directions, from two reads it can already make.
 *
 * The buyer half is `ondo.gallery_agent_roster()`. The seller half is the
 * inventory filtered to unanswered offers — `ondo.gallery_inventory()` already
 * returns `consignorOndoId` and `consignorName`, so grouping by sender needs no
 * read of its own and no schema at all.
 *
 * SELLER OFFERS OPEN AND SELLER AGENTS ARE TWO COUNTS OF THE SAME ROWS, cut
 * differently: how many pictures are waiting on a decision, and how many people
 * are waiting for one. Both are arithmetic over the list below them, which is
 * the rule `TileRow` documents — nothing here records what a count was last
 * month, so nothing may imply it.
 */
export function toGalleryAgentsView(input: {
  placements: GalleryAgentPlacement[] | null;
  offers: ConsignedWork[] | null;
  images: Map<string, string>;
  side: AgentSide;
  openKeys: ReadonlySet<string>;
  workBase: string;
}): GalleryAgentsView {
  const { placements, offers, images, side, openKeys, workBase } = input;

  if (placements === null || offers === null) {
    return { kind: "loading", message: GALLERY_AGENTS_COPY.loading };
  }

  const buyers = groupBuyers(placements, images, openKeys, workBase);
  const sellers = groupSellers(offers, images, openKeys, workBase);

  return {
    kind: "ready",
    tiles: toTiles(buyers, sellers, placements.length),
    side,
    buyerLabel: GALLERY_AGENTS_COPY.buyerChip,
    sellerLabel: `${GALLERY_AGENTS_COPY.sellerChip} · ${sellers.length}`,
    buyers,
    sellers,
    empty: emptyFor(side, buyers.length, sellers.length),
  };
}

/**
 * The three counts across the top.
 *
 * `works out` counts MANDATE ROWS, not distinct works: one picture sent to two
 * agents is out twice, which is what a gallery chasing it needs to know.
 */
function toTiles(
  buyers: AgentGroup[],
  sellers: SellerGroup[],
  placementCount: number,
): Tile[] {
  const offers = sellers.reduce((total, group) => total + group.rows.length, 0);

  return [
    {
      key: "buyers",
      label: GALLERY_AGENTS_COPY.tileBuyers,
      value: String(buyers.length),
      note: plural(placementCount, GALLERY_AGENTS_COPY.tileBuyersNote),
    },
    {
      key: "offers",
      label: GALLERY_AGENTS_COPY.tileOffers,
      value: String(offers),
      note: GALLERY_AGENTS_COPY.tileOffersNote,
    },
    {
      key: "sellers",
      label: GALLERY_AGENTS_COPY.tileSellers,
      value: String(sellers.length),
      note: GALLERY_AGENTS_COPY.tileSellersNote,
    },
  ];
}

function groupBuyers(
  placements: GalleryAgentPlacement[],
  images: Map<string, string>,
  openKeys: ReadonlySet<string>,
  workBase: string,
): AgentGroup[] {
  const groups = new Map<string, AgentGroup>();

  for (const row of placements) {
    let group = groups.get(row.ondoId);
    if (!group) {
      group = {
        key: row.ondoId,
        ondoId: row.ondoId,
        name: row.displayName,
        initials: initialsOf(row.displayName),
        meta: `${row.isOrg ? GALLERY_AGENTS_COPY.firm : GALLERY_AGENTS_COPY.person} · ${row.ondoId}`,
        verified: row.verified,
        rows: [],
        // Open by default. A collapsed list of agents with nothing under it
        // hides the only thing the screen is about — the opposite of the agent's
        // own client panels, where a reader has many clients and few works each.
        open: !openKeys.has(closedKey(row.ondoId)),
      };
      groups.set(row.ondoId, group);
    }

    group.rows.push({
      mandateId: row.mandateId,
      workId: row.workId,
      href: `${workBase}/${row.workId}`,
      title: row.title,
      attribution: [row.attributedName, row.year].filter(Boolean).join(" · "),
      sent: sentLine(row.createdAt, row.note),
      imageUrl: images.get(row.workId) ?? null,
      canEnd: row.canEnd,
    });
  }

  return [...groups.values()];
}

function groupSellers(
  offers: ConsignedWork[],
  images: Map<string, string>,
  openKeys: ReadonlySet<string>,
  workBase: string,
): SellerGroup[] {
  const groups = new Map<string, SellerGroup>();

  for (const work of offers) {
    let group = groups.get(work.consignorOndoId);
    if (!group) {
      group = {
        key: work.consignorOndoId,
        ondoId: work.consignorOndoId,
        name: work.consignorName,
        initials: initialsOf(work.consignorName),
        meta: `${GALLERY_AGENTS_COPY.offering} · ${work.consignorOndoId}`,
        rows: [],
        open: !openKeys.has(closedKey(work.consignorOndoId)),
      };
      groups.set(work.consignorOndoId, group);
    }

    group.rows.push({
      consignmentId: work.consignmentId,
      workId: work.workId,
      href: `${workBase}/${work.workId}`,
      title: work.title,
      attribution: [work.attributedName, work.year].filter(Boolean).join(" · "),
      imageUrl: images.get(work.workId) ?? null,
    });
  }

  return [...groups.values()];
}

/**
 * 'Sent 22 July 2026 · Client viewing in New York, wk of 28 Jul.'
 *
 * The note is the gallery's own free text and is printed as written. When there
 * is none the line is the date alone rather than a trailing separator.
 */
function sentLine(createdAt: string, note: string | null): string {
  const when = formatLongDay(createdAt);
  const sent = `${GALLERY_AGENTS_COPY.sent} ${when}`;
  return note ? `${sent} · ${note}` : sent;
}

/**
 * Which panels the reader has folded away.
 *
 * Keyed on "closed", not "open", so that an agent appearing for the first time
 * after a re-read is open rather than hidden — a set of open keys would make
 * every new arrival invisible until it was clicked.
 */
export function closedKey(ondoId: string): string {
  return `closed:${ondoId}`;
}

function emptyFor(side: AgentSide, buyers: number, sellers: number): string | null {
  if (side === "buyer") return buyers === 0 ? GALLERY_AGENTS_COPY.noBuyers : null;
  return sellers === 0 ? GALLERY_AGENTS_COPY.noSellers : null;
}

/**
 * Why the screen could not load.
 *
 * A 403 is not an error here in the way a 500 is: it means this account holds no
 * gallery seat, which the rail should already have prevented. It still says so
 * plainly rather than showing an empty list, because an empty list would assert
 * that the firm has placed nothing.
 */
export function describeAgentsError(error: unknown): string {
  if (error instanceof ApiError && error.status === 403) {
    return GALLERY_AGENTS_COPY.forbidden;
  }
  return GALLERY_AGENTS_COPY.failed;
}
